import React from 'react'
// libs
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
// hooks
import { useOfflineQueue } from 'hooks/offline'
import { useFormInitialValues, useRiskPermissions, useShowModal } from 'hooks/index'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// constants
import { Modals } from 'constants/modals'
// types
import { IRisk } from 'types/graphql'
// store
import { removeRisk } from 'store/reducers/offlineRisk'
// components
import RiskSkeleton from './components/RiskSkeleton'
import RiskControlButtons from './components/ControllButton'
// view components
import Risk from 'containers/Risks/RiskView/Risk'
// views
import { ButtonsWrapper } from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/Properties/components/Property/view'

export const RiskContainerWrapper = styled.div`
  padding: 8px;
  position: relative;
  min-width: 100%;
  width: fit-content;
  display: flex;
  flex-direction: row;
  font-size: 14px;
  gap: 16px;

  & > ${ButtonsWrapper} {
    margin-left: auto;
    position: sticky;
    right: 0;
    top: 44px;
  }
`

interface Props {
  risk: IRisk
  editRisk: (openInTab?: boolean) => void
  deleteRisk: () => Promise<void>
  isScheduled: boolean
}

const RiskContainer: React.FC<Props> = ({ risk, isScheduled, deleteRisk, editRisk }) => {
  const dispatch = useDispatch()

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_RISK_MODAL })

  const { isLoading } = React.useContext(RiskTemplateAPIContext)

  const { mutations, removeMutation } = useOfflineQueue()

  const { initialValues, isInitialValuesLoaded } = useFormInitialValues(isLoading, risk.fieldValueMap, {
    risk,
    customColumns: risk.customColumns
  })

  const permissions = useRiskPermissions(risk)

  const form = `RISK_${risk.id}_FORM`
  const importedRisk = !!risk.realRiskId
  const isTemplateActive = !risk.riskTemplate.archived
  const canEdit = isTemplateActive && permissions.riskModify && risk.canUpdateFromSystemProcesses
  const canDelete = isTemplateActive && !importedRisk && permissions.riskDelete && risk.canDeleteFromSystemProcesses

  const deleteOfflineRisk = () => {
    const mutationsIds = mutations
      .filter((item) => item.tempId === risk.id || item.dependsOn === risk.id)
      .map((item) => item.id)

    removeMutation(mutationsIds)
    dispatch(removeRisk(risk.id))
    deleteRisk()
  }

  const onDeleteClick = () => {
    showModal({
      width: 'sm',
      handleConfirm: navigator.onLine ? deleteRisk : deleteOfflineRisk
    })
  }

  if (isLoading || !isInitialValuesLoaded) {
    return <RiskSkeleton />
  }

  return (
    <RiskContainerWrapper data-testid='risk-view-risk' data-entity-id={risk.id}>
      <Risk risk={risk} form={form} isScheduled={isScheduled} initialValues={initialValues} />
      <RiskControlButtons
        risk={risk}
        canEdit={canEdit}
        canDelete={canDelete}
        onEditClick={editRisk}
        canView={!importedRisk}
        isImported={importedRisk}
        onDeleteClick={onDeleteClick}
      />
    </RiskContainerWrapper>
  )
}

export default RiskContainer
